import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, ChevronDown, ChevronUp, Trash2, Flag, Calendar, MapPin, Search, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import FeedbackPopup, { shouldShowFeedback } from '../components/FeedbackPopup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Scores = () => {
  const { user } = useAuth();
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedMode, setExpandedMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [savedScore, setSavedScore] = useState(null); // For share prompt after save

  // Course search state
  const [courseQuery, setCourseQuery] = useState('');
  const [nearbyCourses, setNearbyCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const courseInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const [form, setForm] = useState({
    date_played: new Date().toISOString().split('T')[0],
    score: '', holes_played: '18',
    putts: '', fairways_hit: '', fairways_total: '14',
    greens_in_reg: '', greens_total: '18', notes: ''
  });

  // Fetch nearby courses based on user location
  const fetchNearbyCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      // Try to get user's current position
      let lat, lng;
      if (user?.latitude && user?.longitude) {
        lat = user.latitude;
        lng = user.longitude;
      } else {
        // Fallback: try browser geolocation
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
          );
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch {
          lat = 30.2672;
          lng = -97.7431;
        }
      }
      const res = await axios.get(`${API}/golf/courses/nearby`, { params: { lat, lng } });
      const courses = res.data?.courses || [];
      setNearbyCourses(courses);
      setFilteredCourses(courses);
    } catch {
      /* silent */
    } finally {
      setLoadingCourses(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNearbyCourses();
  }, [fetchNearbyCourses]);

  // Filter courses as user types
  useEffect(() => {
    if (!courseQuery.trim()) {
      setFilteredCourses(nearbyCourses);
      return;
    }
    const q = courseQuery.toLowerCase();
    const filtered = nearbyCourses.filter(c =>
      (c.course_name || c.club_name || '').toLowerCase().includes(q) ||
      (c.location?.address || '').toLowerCase().includes(q) ||
      (c.location?.city || '').toLowerCase().includes(q)
    );
    setFilteredCourses(filtered);
  }, [courseQuery, nearbyCourses]);

  // Also search the course API if query is long enough and no local matches
  useEffect(() => {
    if (courseQuery.length < 3) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/golf/courses/search`, { params: { q: courseQuery } });
        const apiCourses = res.data?.courses || [];
        if (apiCourses.length > 0) {
          setFilteredCourses(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const newCourses = apiCourses
              .filter(c => !existingIds.has(c.id))
              .map(c => ({
                id: c.id,
                course_name: c.course_name || c.club_name,
                club_name: c.club_name,
                location: c.location || {},
                source: 'search',
              }));
            return [...prev, ...newCourses];
          });
        }
      } catch { /* silent */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [courseQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          courseInputRef.current && !courseInputRef.current.contains(e.target)) {
        setShowCourseDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchScores = async () => {
    try {
      const res = await axios.get(`${API}/golf/manual-scores`);
      setScores(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchScores(); }, []);

  const handleCourseSelect = (course) => {
    const name = course.course_name || course.club_name;
    setSelectedCourse(course);
    setCourseQuery(name);
    setShowCourseDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse) { toast.error('Please select a course from the list'); return; }
    if (!form.score) { toast.error('Please enter your score'); return; }
    setSubmitting(true);
    try {
      const courseName = selectedCourse.course_name || selectedCourse.club_name;
      const payload = {
        course_name: courseName,
        date_played: form.date_played,
        score: parseInt(form.score),
        holes_played: parseInt(form.holes_played) || 18,
        putts: form.putts ? parseInt(form.putts) : null,
        fairways_hit: form.fairways_hit ? parseInt(form.fairways_hit) : null,
        fairways_total: form.fairways_total ? parseInt(form.fairways_total) : null,
        greens_in_reg: form.greens_in_reg ? parseInt(form.greens_in_reg) : null,
        greens_total: form.greens_total ? parseInt(form.greens_total) : null,
        notes: form.notes || null,
      };
      await axios.post(`${API}/golf/manual-score`, payload);

      // Store score info for share prompt
      const scoreInfo = {
        score: form.score,
        courseName,
        holes: parseInt(form.holes_played) || 18,
        notes: form.notes,
      };
      setSavedScore(scoreInfo);

      setShowForm(false);
      setSelectedCourse(null);
      setCourseQuery('');
      setForm({ date_played: new Date().toISOString().split('T')[0], score: '', holes_played: '18', putts: '', fairways_hit: '', fairways_total: '14', greens_in_reg: '', greens_total: '18', notes: '' });
      fetchScores();

      // Check if we should show feedback popup (delayed)
      if (shouldShowFeedback('post_score')) {
        setTimeout(() => setShowFeedback(true), 3000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save score');
    } finally { setSubmitting(false); }
  };

  const shareScoreToFeed = async (scoreInfo) => {
    try {
      const postContent = `Shot a ${scoreInfo.score} at ${scoreInfo.courseName} (${scoreInfo.holes} holes)${scoreInfo.notes ? ` - ${scoreInfo.notes}` : ''}`;
      await axios.post(`${API}/social/posts`, { content: postContent });
      toast.success('Shared to your feed!');
    } catch {
      toast.error('Failed to share to feed');
    }
    setSavedScore(null);
  };

  const shareExistingScore = async (score) => {
    try {
      const postContent = `Shot a ${score.score} at ${score.course_name} (${score.holes_played} holes)${score.notes ? ` - ${score.notes}` : ''}`;
      await axios.post(`${API}/social/posts`, { content: postContent });
      toast.success('Shared to your feed!');
    } catch {
      toast.error('Failed to share to feed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/golf/manual-score/${id}`);
      setScores(prev => prev.filter(s => s.id !== id));
      toast.success('Score deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const inputCls = "h-11 rounded-lg border border-border bg-card focus:bg-card transition-colors";

  const recentScores = scores.slice(0, 10);
  const avg = recentScores.length > 0 ? Math.round(recentScores.reduce((a, s) => a + s.score, 0) / recentScores.length) : null;
  const best = recentScores.length > 0 ? Math.min(...recentScores.map(s => s.score)) : null;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="scores-page">
      {/* Header */}
      <div className="px-4 pt-5 pb-3 sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-2xl font-bold text-foreground">My Scores</h1>
          <Button
            onClick={() => { setShowForm(!showForm); if (!showForm) setSelectedCourse(null); setCourseQuery(''); }}
            className="rounded-full gap-1.5 text-sm font-semibold px-4"
            data-testid="add-score-btn"
          >
            <Plus className="w-4 h-4" /> Add Score
          </Button>
        </div>

        {scores.length > 0 && (
          <div className="flex gap-3 mt-3">
            <div className="flex-1 rounded-xl p-3 text-center bg-primary/5">
              <p className="text-[10px] font-bold uppercase text-muted-foreground">Rounds</p>
              <p className="text-xl font-black text-foreground">{scores.length}</p>
            </div>
            <div className="flex-1 rounded-xl p-3 text-center bg-primary/5">
              <p className="text-[10px] font-bold uppercase text-muted-foreground">Average</p>
              <p className="text-xl font-black text-foreground">{avg}</p>
            </div>
            <div className="flex-1 rounded-xl p-3 text-center bg-secondary/10">
              <p className="text-[10px] font-bold uppercase text-muted-foreground">Best</p>
              <p className="text-xl font-black text-secondary">{best}</p>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {/* Add Score Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="rounded-xl border border-border p-5 mb-5 bg-card" data-testid="score-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg font-bold text-foreground">Log a Round</h2>
              <button type="button" onClick={() => setExpandedMode(!expandedMode)} className="text-xs font-semibold flex items-center gap-1 text-primary" data-testid="toggle-expanded-btn">
                {expandedMode ? 'Simple' : 'Detailed'} {expandedMode ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>

            <div className="space-y-3">
              {/* Course Search */}
              <div className="space-y-1 relative">
                <Label className="text-xs font-semibold uppercase text-muted-foreground">Course *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  {loadingCourses && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                  <Input
                    ref={courseInputRef}
                    placeholder="Search for a course..."
                    value={courseQuery}
                    onChange={e => { setCourseQuery(e.target.value); setSelectedCourse(null); setShowCourseDropdown(true); }}
                    onFocus={() => setShowCourseDropdown(true)}
                    className={`${inputCls} pl-10`}
                    data-testid="course-search-input"
                  />
                </div>
                {selectedCourse && (
                  <div className="flex items-center gap-1.5 px-2 py-1">
                    <MapPin className="w-3 h-3 text-primary" />
                    <span className="text-xs font-medium text-primary">
                      {selectedCourse.location?.address || selectedCourse.location?.city || 'Selected'}
                    </span>
                  </div>
                )}

                {/* Course dropdown */}
                {showCourseDropdown && !selectedCourse && (
                  <div
                    ref={dropdownRef}
                    className="absolute left-0 right-0 top-full z-30 mt-1 rounded-xl shadow-xl border border-border max-h-56 overflow-y-auto bg-card"
                    data-testid="course-dropdown"
                  >
                    {filteredCourses.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                        {loadingCourses ? 'Loading nearby courses...' : courseQuery.length > 0 ? 'No courses found. Try a different search.' : 'Loading...'}
                      </div>
                    ) : (
                      filteredCourses.slice(0, 15).map((course, idx) => (
                        <button
                          key={course.id || idx}
                          type="button"
                          onClick={() => handleCourseSelect(course)}
                          className="w-full px-4 py-3 text-left transition-colors flex items-start gap-3 border-b border-border last:border-b-0 hover:bg-muted/50"
                          data-testid={`course-option-${idx}`}
                        >
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {course.course_name || course.club_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {[course.location?.address, course.location?.city, course.location?.state].filter(Boolean).join(', ')}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase text-muted-foreground">Score *</Label>
                  <Input type="number" placeholder="72" value={form.score} onChange={e => setForm({ ...form, score: e.target.value })} required className={inputCls} data-testid="score-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase text-muted-foreground">Holes</Label>
                  <Select value={form.holes_played} onValueChange={v => setForm({ ...form, holes_played: v })}>
                    <SelectTrigger className="h-11 rounded-lg border-border" data-testid="holes-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="9">9</SelectItem>
                      <SelectItem value="18">18</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase text-muted-foreground">Date</Label>
                  <Input type="date" value={form.date_played} onChange={e => setForm({ ...form, date_played: e.target.value })} className={inputCls} data-testid="date-input" />
                </div>
              </div>

              {/* Expanded fields */}
              {expandedMode && (
                <div className="space-y-3 pt-2 mt-2 border-t border-border">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground">Putts</Label>
                      <Input type="number" placeholder="32" value={form.putts} onChange={e => setForm({ ...form, putts: e.target.value })} className={inputCls} data-testid="putts-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground">FIR</Label>
                      <div className="flex gap-1">
                        <Input type="number" placeholder="8" value={form.fairways_hit} onChange={e => setForm({ ...form, fairways_hit: e.target.value })} className={`${inputCls} w-1/2`} data-testid="fir-hit-input" />
                        <span className="self-center text-xs text-muted-foreground">/</span>
                        <Input type="number" placeholder="14" value={form.fairways_total} onChange={e => setForm({ ...form, fairways_total: e.target.value })} className={`${inputCls} w-1/2`} data-testid="fir-total-input" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground">GIR</Label>
                      <div className="flex gap-1">
                        <Input type="number" placeholder="10" value={form.greens_in_reg} onChange={e => setForm({ ...form, greens_in_reg: e.target.value })} className={`${inputCls} w-1/2`} data-testid="gir-hit-input" />
                        <span className="self-center text-xs text-muted-foreground">/</span>
                        <Input type="number" placeholder="18" value={form.greens_total} onChange={e => setForm({ ...form, greens_total: e.target.value })} className={`${inputCls} w-1/2`} data-testid="gir-total-input" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase text-muted-foreground">Notes</Label>
                    <Textarea placeholder="How did it go?" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-lg border border-border bg-card min-h-16" data-testid="notes-input" />
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full h-11 rounded-lg font-semibold text-sm btn-press" disabled={submitting || !selectedCourse} data-testid="submit-score-btn">
                {submitting ? 'Saving...' : 'Save Score'}
              </Button>
            </div>
          </form>
        )}

        {/* Scores list */}
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 rounded-xl animate-pulse bg-muted" />)}</div>
        ) : scores.length === 0 && !showForm ? (
          <div className="text-center py-16">
            <Flag className="w-12 h-12 mx-auto mb-3 text-muted" />
            <h3 className="font-serif text-xl font-bold mb-2 text-foreground">No Scores Yet</h3>
            <p className="text-sm text-muted-foreground">Tap "Add Score" to log your first round</p>
          </div>
        ) : (
          <div className="space-y-3">
            {scores.map(s => (
              <div key={s.id} className="rounded-xl border border-border p-4 bg-card" data-testid={`score-card-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm text-foreground">{s.course_name}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs flex items-center gap-1 text-muted-foreground">
                        <Calendar className="w-3 h-3" /> {new Date(s.date_played).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="text-xs text-muted-foreground">{s.holes_played} holes</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-black text-foreground">{s.score}</p>
                    <button onClick={() => handleDelete(s.id)} className="opacity-30 hover:opacity-70 transition-opacity" data-testid={`delete-score-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {(s.putts || s.fairways_hit != null || s.greens_in_reg != null) && (
                  <div className="flex gap-3 mt-3 pt-3 border-t border-border">
                    {s.putts && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground">{s.putts} putts</span>}
                    {s.fairways_hit != null && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground">FIR {s.fairways_hit}/{s.fairways_total || 14}</span>}
                    {s.greens_in_reg != null && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground">GIR {s.greens_in_reg}/{s.greens_total || 18}</span>}
                  </div>
                )}
                {s.notes && <p className="text-xs mt-2 italic text-muted-foreground">{s.notes}</p>}
                <div className="mt-3 pt-3 border-t border-border">
                  <button
                    onClick={() => shareExistingScore(s)}
                    className="flex items-center gap-1.5 text-xs font-semibold transition-colors hover:opacity-80 text-primary"
                    data-testid={`share-score-${s.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share to Feed
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Navigation />
      {/* Share prompt after saving score */}
      {savedScore && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="share-prompt">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setSavedScore(null)} />
          <div className="relative w-full max-w-sm mx-4 rounded-2xl overflow-hidden bg-card shadow-2xl">
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-muted">
                <Flag className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-serif text-xl font-bold mb-1 text-foreground">Score Saved!</h3>
              <p className="text-sm mb-1 text-muted-foreground">
                {savedScore.score} at {savedScore.courseName}
              </p>
              <p className="text-sm mb-5 text-muted-foreground">Want to share this round to your feed?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setSavedScore(null)}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold border border-border text-muted-foreground"
                  data-testid="share-prompt-skip"
                >
                  Done
                </button>
                <button
                  onClick={() => shareScoreToFeed(savedScore)}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-primary text-primary-foreground"
                  data-testid="share-prompt-share"
                >
                  <Share2 className="w-4 h-4" /> Share to Feed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showFeedback && <FeedbackPopup context="post_score" onClose={() => setShowFeedback(false)} />}
    </div>
  );
};

export default Scores;
